import React, { createContext, useContext, useEffect, useState } from "react";
import { useAuth } from "./AuthContext";
import { getWorkspaceOwner } from "./teams/workspace";
import { LoadingOverlay } from "./shared/LoadingOverlay";
import { eventLivePageBase, formatPriceWithCurrency } from "./globalUtils";
import { CAMERA_PRODUCT } from "./rooms/multicam";
import { t, tn } from "@/localization";

const buyContext = createContext();

const priceFallbackProductIds = [
  "events_storage_1y",
  "events_storage_1m",
  "events_storage_10y",
  "single_business_new",
  "specialeventnew",
  "specialevent50",
];
// Priced only as themselves: no other product's price stands in for an
// extra camera's (components/rooms/multicam.js).
const exactPriceProductIds = new Set([CAMERA_PRODUCT]);
const creditPackageProductIds = new Set([
  "package_5",
  "package_5_new",
  "package_10",
  "package_10_new",
  "package_25",
  "package_25_new",
  "package_50",
  "package_50_new",
]);

const normalizeCountryCode = (countryCode) => {
  if (typeof countryCode !== "string") {
    return "";
  }

  const normalized = countryCode.trim().toUpperCase();
  return /^[A-Z]{2}$/.test(normalized) ? normalized : "";
};

const parseNumericPrice = (rawPrice) => {
  if (typeof rawPrice === "number") {
    return Number.isFinite(rawPrice) ? rawPrice : NaN;
  }

  if (typeof rawPrice !== "string") {
    return NaN;
  }

  let normalized = rawPrice.replace(/[^\d,.-]/g, "");
  if (!normalized) {
    return NaN;
  }

  const hasComma = normalized.includes(",");
  const hasDot = normalized.includes(".");

  if (hasComma && hasDot) {
    if (normalized.lastIndexOf(",") > normalized.lastIndexOf(".")) {
      normalized = normalized.replace(/\./g, "").replace(/,/g, ".");
    } else {
      normalized = normalized.replace(/,/g, "");
    }
  } else if (hasComma) {
    normalized = normalized.replace(/,/g, ".");
  }

  const parsed = parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : NaN;
};

const normalizeQuantity = (quantity) => {
  const parsed = Number(quantity);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
};

const getPriceApiUrl = (countryCode) => {
  const normalizedCountryCode = normalizeCountryCode(countryCode);
  const query = normalizedCountryCode
    ? `?country=${encodeURIComponent(normalizedCountryCode)}`
    : "";
  return `${eventLivePageBase}api/price${query}`;
};

const getCountryOverrideFromUrl = () => {
  if (typeof window === "undefined") {
    return "";
  }

  const urlParams = new URLSearchParams(window.location.search);
  return normalizeCountryCode(urlParams.get("country"));
};

function useProvideBuy() {
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState({
    events_storage_1m: { currency: "USD", price: 50, country: "US" },
    events_storage_1y: { currency: "USD", price: 50, country: "US" },
    events_storage_10y: { currency: "USD", price: 50, country: "US" },
    specialevent50: { currency: "USD", price: 50, country: "US" },
  });
  const [countryOverride, setCountryOverride] = useState("");
  const [pricingReady, setPricingReady] = useState(false);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        setPricingReady(false);
        const overrideCountryCode = getCountryOverrideFromUrl();
        setCountryOverride(overrideCountryCode);
        const response = await fetch(getPriceApiUrl(overrideCountryCode));
        const data = await response.json();
        setProducts(data);
      } catch (error) {
        console.error("Error:", error);
      } finally {
        setPricingReady(true);
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  const { broadcaster } = useAuth();

  const getProductData = (productId) => {
    if (products[productId]) {
      return products[productId];
    }

    if (exactPriceProductIds.has(productId)) {
      return null;
    }

    for (const fallbackId of priceFallbackProductIds) {
      if (products[fallbackId]) {
        return products[fallbackId];
      }
    }

    return products.specialevent50;
  };

  const getResolvedProductId = (productId) => {
    if (products[productId] || exactPriceProductIds.has(productId)) {
      return productId;
    }

    for (const fallbackId of priceFallbackProductIds) {
      if (products[fallbackId]) {
        return fallbackId;
      }
    }

    return "specialevent50";
  };

  const getCurrencyPrice = (productId, quantity = 1) => {
    // get https://evt.live/api/price
    //{"country":"PT","currency":"EUR","price":50}
    // slug,

    const data = getProductData(productId);
    if (!data) return "";
    const normalizedQuantity = normalizeQuantity(quantity);
    const unitPrice = parseNumericPrice(data.price);
    const totalPrice = Number.isFinite(unitPrice)
      ? unitPrice * normalizedQuantity
      : data.price;
    let formattedPrice = formatPriceWithCurrency(totalPrice, data.currency);
    return formattedPrice;
  };

  // Several products bought with one tap, `lines` [{ productId, quantity }]
  // (a camera group's main camera and its cameras), added up. "" while a
  // price is missing, or when they aren't in one currency.
  const getCurrencyTotal = (lines = []) => {
    let total = 0;
    let currency = null;
    for (const { productId, quantity } of lines) {
      if (!quantity) continue;
      const data = getProductData(productId);
      const price = parseNumericPrice(data?.price);
      if (!Number.isFinite(price)) return "";
      const lineCurrency = data.currency || "USD";
      if (currency && lineCurrency !== currency) return "";
      currency = lineCurrency;
      total += price * normalizeQuantity(quantity);
    }
    return currency ? formatPriceWithCurrency(total, currency) : "";
  };

  const getOriginalPrice = (productId) => {
    const data = getProductData(productId);
    const price = parseNumericPrice(data?.price);
    return Number.isFinite(price) ? price : 0;
  };

  const getPricePerItem = (productId) => {
    const data = getProductData(productId);
    if (!data) return "";
    return formatPriceWithCurrency(data.pricePer, data.currency);
  };

  const getProductCurrency = (productId) => {
    const data = getProductData(productId);
    return data?.currency || "USD";
  };

  const getDiscountPercent = (productId) => {
    const data = getProductData(productId);
    if (!data) return 0;
    return Math.round(
      ((data.originalPrice - data.price) / data.originalPrice) * 100
    );
  };

  const getOriginalCurrencyPrice = (productId, quantity = 1) => {
    const data = getProductData(productId);
    const original = parseNumericPrice(data?.originalPrice);
    if (!Number.isFinite(original) || original <= 0) return "";
    const normalizedQuantity = normalizeQuantity(quantity);
    return formatPriceWithCurrency(
      original * normalizedQuantity,
      data.currency || "USD"
    );
  };

  const getSavingsPercent = (productId, originalProductId) => {
    const data = getProductData(productId);
    const originalData = getProductData(originalProductId);
    if (!data || !originalData) return 0;
    const basePrice = originalData.pricePer;
    const price = data.pricePer;
    const savingsPercent = Math.round(((basePrice - price) / basePrice) * 100);
    return savingsPercent;
  };

  const getBuyNowText = (productId, days = 1, days_paid = 0) => {
    const currencyPrice = getCurrencyPrice(productId);
    const daysToPay = days - days_paid;

    if (daysToPay == 1 && days_paid == 0) {
      return t("{price} One Time", { price: currencyPrice });
    }

    if (days_paid > 0) {
      // "day" for anything up to 1, as English always had it.
      return tn(
        "{count} extra day: {price} / day",
        "{count} extra days: {price} / day",
        daysToPay > 1 ? daysToPay : 1,
        { count: daysToPay, price: currencyPrice }
      );
    } else {
      return t("{days}-Day Event: {price} / day", {
        days,
        price: currencyPrice,
      });
    }
  };

  // Event handler for when the tab or page becomes visible
  const handleVisibilityChange = () => {
    if (document.visibilityState === "visible") {
      setLoading(false);
      // Remove the event listener once the loading state has been updated
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      document.removeEventListener(
        "webkitvisibilitychange",
        handleVisibilityChange
      );
    }
  };

  // rooms: the checkout is for a camera group's main camera and every locked
  // camera; evt.live works out which and charges for each, the cameras at
  // CAMERA_PRODUCT, so the app's `followUp` (a second store purchase) isn't
  // needed here (components/rooms/cameraCheckout.js).
  const purchase = async ({
    productId,
    slug,
    title,
    quantity = 1,
    rooms = false,
  }) => {
    if (!pricingReady) {
      return;
    }

    try {
      setLoading(true);
      const isDevelopment = process.env.NODE_ENV === "development";
      const resolvedProductId = getResolvedProductId(productId);
      const productData = getProductData(resolvedProductId);
      const country = normalizeCountryCode(
        countryOverride || productData?.country
      );
      const normalizedQuantity = normalizeQuantity(quantity);
      const checkoutQuantity = creditPackageProductIds.has(resolvedProductId)
        ? 1
        : normalizedQuantity;
      const queryParams = [`price=${encodeURIComponent(resolvedProductId)}`];

      if (isDevelopment) {
        queryParams.push("stripe_test=true");
      }
      if (country) {
        queryParams.push(`country=${encodeURIComponent(country)}`);
      }
      if (checkoutQuantity > 1) {
        queryParams.push(`quantity=${encodeURIComponent(checkoutQuantity)}`);
      }
      if (rooms && slug) {
        queryParams.push("rooms=1");
      }

      const queryString = queryParams.join("&");

      if (slug) {
        // Teams: the event's account (the one you work in); credit packs
        // below are always bought for your own account.
        const eventOwner = getWorkspaceOwner() || broadcaster;
        document.location.href = `${eventLivePageBase}api/unlock/${eventOwner}/${slug}?${queryString}`;
      } else {
        document.location.href = `${eventLivePageBase}api/buypack/${broadcaster}/?${queryString}`;
      }
      // Listen for the tab or page to become visible
      document.addEventListener("visibilitychange", handleVisibilityChange);
      document.addEventListener(
        "webkitvisibilitychange",
        handleVisibilityChange
      );
    } catch (err) {
      console.error(err);
    }
  };

  return {
    getCurrencyPrice,
    getCurrencyTotal,
    getOriginalPrice,
    getOriginalCurrencyPrice,
    getProductCurrency,
    getSavingsPercent,
    getDiscountPercent,
    getPricePerItem,
    purchase,
    loading,
    getBuyNowText,
    pricingReady,
  };
}

export function ProvideBuy({ children }) {
  const buy = useProvideBuy();
  return (
    <buyContext.Provider value={buy}>
      {children}
      <LoadingOverlay loading={buy.loading} />
    </buyContext.Provider>
  );
}

export const useBuy = () => {
  return useContext(buyContext);
};
